import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../utils/api";
import Swal from "sweetalert2";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize Session: try API first, fall back to localStorage
  useEffect(() => {
    const fetchProfile = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!storedToken) {
        // No token at all — not logged in
        if (storedUser) setUser(JSON.parse(storedUser));
        setLoading(false);
        return;
      }

      try {
        const res = await api.get("/api/auth/profile");
        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));
      } catch (err) {
        if (err.response?.status === 401) {
          // Token is invalid/expired — clear everything
          localStorage.removeItem("user");
          localStorage.removeItem("token");
        } else {
          // Network/server error — keep existing session from localStorage
          if (storedUser) setUser(JSON.parse(storedUser));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/api/auth/login", { email, password });
      localStorage.setItem("user", JSON.stringify(res.data.user));
      localStorage.setItem("token", res.data.token);
      setUser(res.data.user);
      
      Swal.fire({
        icon: 'success',
        title: 'Welcome Back!',
        text: 'You have logged in successfully.',
        timer: 2000,
        showConfirmButton: false,
        background: '#0b0b0b',
        color: '#fff'
      });
      return true;
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: err.response?.data?.message || "Invalid credentials",
        background: '#0b0b0b',
        color: '#fff'
      });
      return false;
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      await api.post("/api/auth/register", { name, email, password, phone });
      
      Swal.fire({
        icon: 'success',
        title: 'Registration Successful',
        text: 'Please login to continue.',
        timer: 3000,
        showConfirmButton: false,
        background: '#0b0b0b',
        color: '#fff'
      });
      return true;
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: err.response?.data?.message || "Something went wrong",
        background: '#0b0b0b',
        color: '#fff'
      });
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.get("/api/auth/logout");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setUser(null);
      
      Swal.fire({
        icon: 'info',
        title: 'Logged Out',
        text: 'You have been logged out successfully.',
        timer: 1500,
        showConfirmButton: false,
        background: '#0b0b0b',
        color: '#fff'
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Logout Failed',
        background: '#0b0b0b',
        color: '#fff'
      });
    }
  };

  const updateProfile = async (name, phone) => {
    try {
      const res = await api.put("/api/auth/update-profile", { name, phone });
      setUser(res.data.user);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      
      Swal.fire({
        icon: 'success',
        title: 'Profile Updated!',
        text: 'Your information has been saved.',
        timer: 1500,
        showConfirmButton: false,
        background: '#0b0b0b',
        color: '#fff'
      });
      return true;
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.message || "Could not update profile",
        background: '#0b0b0b',
        color: '#fff'
      });
      return false;
    }
  };

  const forgotPassword = async (email, phone) => {
    try {
      await api.post("/api/auth/forgot-password", { email, phone });
      return true;
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: err.response?.data?.message || "User not found",
        background: '#0b0b0b',
        color: '#fff'
      });
      return false;
    }
  };

  const resetPassword = async (email, phone, newPassword) => {
    try {
      await api.post("/api/auth/reset-password", { email, phone, newPassword });
      Swal.fire({
        icon: 'success',
        title: 'Password Reset!',
        text: 'Please login with your new password.',
        background: '#0b0b0b',
        color: '#fff'
      });
      return true;
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Reset Failed',
        text: err.response?.data?.message || "Could not reset password",
        background: '#0b0b0b',
        color: '#fff'
      });
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
